// Dedicated content & imagery library for the Level Up XR Programme.
// Every image is INTENTIONALLY chosen for its section and is used NOWHERE else
// on the site. Swap URLs here when real TOK cohort photography arrives.

export const LEVEL_UP_IMG = {
  hero:
    "https://images.pexels.com/photos/8728566/pexels-photo-8728566.jpeg?auto=compress&cs=tinysrgb&w=1920",
  skills:
    "https://images.pexels.com/photos/8728382/pexels-photo-8728382.jpeg?auto=compress&cs=tinysrgb&w=1400",
  projection:
    "https://images.pexels.com/photos/3045825/pexels-photo-3045825.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ai:
    "https://images.pexels.com/photos/8438918/pexels-photo-8438918.jpeg?auto=compress&cs=tinysrgb&w=1400",
  unreal:
    "https://images.pexels.com/photos/7915437/pexels-photo-7915437.jpeg?auto=compress&cs=tinysrgb&w=1400",
  performance:
    "https://images.pexels.com/photos/2167395/pexels-photo-2167395.jpeg?auto=compress&cs=tinysrgb&w=1400",
  employability:
    "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1400",
  cohort:
    "https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg?auto=compress&cs=tinysrgb&w=1400",
  // New for expanded page
  whoFor:
    "https://images.pexels.com/photos/7048043/pexels-photo-7048043.jpeg?auto=compress&cs=tinysrgb&w=1400",
  outcomes:
    "https://images.pexels.com/photos/8472876/pexels-photo-8472876.jpeg?auto=compress&cs=tinysrgb&w=1400",
  accreditation:
    "https://images.pexels.com/photos/3760529/pexels-photo-3760529.jpeg?auto=compress&cs=tinysrgb&w=1400",
  partnerships:
    "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

// Who the programme is genuinely built for — speaks directly to audience.
export const LEVEL_UP_FOR = [
  "16–25 year olds finding their voice",
  "Young people not in education, employment or training (NEET)",
  "Creatives underrepresented in tech, performance & immersive industries",
  "Anyone at risk of exclusion or disconnected from traditional pathways",
  "Beginners with no prior experience — and advanced creatives ready to level up",
  "Young people from the global majority hungry to build cultural futures",
];

// Programme content — 12 disciplines / experience tracks
export const LEVEL_UP_TRACKS = [
  { key: "immersive-tech", title: "Immersive technology", desc: "Headsets, sensors, spatial computing — the rig real productions use." },
  { key: "projection", title: "Projection mapping", desc: "Light as a material. Map buildings, bodies, scenes." },
  { key: "ai", title: "AI creative tools", desc: "Generative imagery, story engines and sound — with cultural literacy baked in." },
  { key: "xr-vr", title: "XR & VR experiences", desc: "Author worlds you can step inside. Theatre, training, art." },
  { key: "storytelling", title: "Digital storytelling", desc: "Voice, narrative arc, character — across screens and chambers." },
  { key: "movement", title: "Movement & performance", desc: "Body in space, presence, ensemble. The discipline behind the spectacle." },
  { key: "game", title: "Game interaction", desc: "Mechanics, agency, world rules. Build playable experiences." },
  { key: "music", title: "Music & sound experimentation", desc: "Beat-making, production, spatial audio and live sound design." },
  { key: "cultural", title: "Cultural storytelling", desc: "Diaspora, lineage, heritage — translated into immersive form." },
  { key: "wellbeing", title: "Creative wellbeing", desc: "Practice as care. Breath, ritual, reflection woven through the work." },
  { key: "teamwork", title: "Teamwork & collaboration", desc: "Real productions are team sports. Learn the practice of building together." },
  { key: "leadership", title: "Leadership", desc: "Lead a cohort, run a session, hold a room. Practical, supported, real." },
];

// Outcomes — what changes for the young person by the end
export const LEVEL_UP_OUTCOMES = [
  { h: "Confidence", b: "Speak, perform, present and pitch in front of a room." },
  { h: "Social skills", b: "Real friendships, support networks and creative collaborators." },
  { h: "Collaboration", b: "Practical experience of building inside a small, focused team." },
  { h: "Communication", b: "Articulate your work, your craft and your story — in any room." },
  { h: "Employability", b: "Industry-recognised skills, mentors and direct introductions to studios." },
  { h: "Leadership", b: "Workshop-leading, peer mentoring and cohort facilitation." },
  { h: "Digital skills", b: "XR, AI, projection, sound and game tools — fluent, not surface." },
  { h: "Wellbeing", b: "Practices for nervous system, focus and creative sustainability." },
  { h: "Fitness & movement", b: "Body-led work that builds physical confidence and presence." },
  { h: "Creative industry pathways", b: "Routes into theatre, film, gaming, XR, advertising and beyond." },
];

// Accreditation & Progression
export const LEVEL_UP_PROGRESSION = [
  {
    h: "Certificates of completion",
    b: "Every participant who completes the programme receives a TOK Level Up XR certificate.",
  },
  {
    h: "Accredited creative skills pathways",
    b: "Selected modules can be mapped to accredited creative skills frameworks via our partners.",
  },
  {
    h: "Portfolio development",
    b: "End the programme with a portfolio piece performed/exhibited at our showcase — and ready to share with universities or employers.",
  },
  {
    h: "Industry experience",
    b: "Paid commission opportunities and real-world placements on TOK productions and partner studios.",
  },
  {
    h: "Further education & creative careers",
    b: "Direct support — references, portfolio reviews, applications — into FE, HE and apprenticeships.",
  },
];

export const LEVEL_UP_INSTITUTIONS = [
  {
    name: "University of Kent",
    desc: "Conversations underway with Kent's drama, film and computing faculties for progression routes.",
  },
  {
    name: "Rose Bruford College",
    desc: "Performance, theatre and immersive technology pathways — a natural progression for our cohort.",
  },
  {
    name: "Creative technology training providers",
    desc: "Apprenticeships and technical specialism in XR, AI, projection, game dev and virtual production.",
  },
];

// Partnership ecosystem — who can engage and how
export const LEVEL_UP_PARTNERS = [
  "Youth organisations",
  "Local authorities & councils",
  "Organisations supporting NEET young people",
  "NHS wellbeing & referral pathways",
  "Schools & colleges",
  "Pupil referral units",
  "Alternative education providers",
  "Community organisations",
  "Housing associations",
  "Creative industry partners",
  "Universities",
  "Cultural institutions",
  "Sponsors",
  "Funders & trusts",
];

export const PARTNER_ENGAGEMENT = [
  "Refer participants",
  "Co-deliver programmes",
  "Fund placements",
  "Sponsor workshops",
  "Support outreach & engagement",
  "Host activity at your venue",
  "Commission experiences",
  "Support wellbeing & youth intervention pathways",
];

export const LEVEL_UP_INTERESTS = [
  "Projection mapping",
  "AI creation",
  "Unreal Engine / XR",
  "Performance & storytelling",
  "Sound design",
  "Filmmaking",
  "Game design",
  "Movement & wellbeing",
  "Cultural storytelling",
  "Leadership",
];

// Slug helper — used for stable data-testids
export const slug = (s) =>
  s
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
